import { useState } from "react"
import { useParams } from "react-router"
import { Box, Stack, Typography } from "@mui/material"
import { BoundEditableText } from "lib/@components/editable/editableText"
import { AuthoringBoundEditableRichText } from "authoring/components/authoring-editable-rich-text"
import { Accordion } from "minimals-template/components/accordion"
import { useDarkMode } from "lib/use-dark-mode"
import { createSlot } from "lib/@components/slot/create-slot"
import { AmendsBoundaryButton } from "authoring/routes/project/amends/components/amends-boundary-button"
import { AmendsBoundaryEditor } from "authoring/routes/project/amends/components/amends-boundary-editor"
import { useAmendCollapse } from "authoring/routes/project/amends/components/use-amend-collapse"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { INTRO_CONTENT_AMENDS_KEY, INTRO_NOTE_AMENDS_KEY } from "../amends/lib/constants/amends-keys"
import { AuthoringEditorRejectionBanner } from "./authoring-editor-rejection-banner"
import { NewScheduleMetadataFields } from "./components/new-schedule-metadata-fields"
import { ScheduleCodeEditableField } from "./schedule-code-editable-field"

const CODE_FIELD = "code"
const TITLE_FIELD = "title"

export const ScheduleEditorFormTop = createSlot("Authoring.ScheduleEditorFormTop")

export function ScheduleEditorForm() {
    const [openIntro, setOpenIntro] = useState(true)
    const isDark = useDarkMode()
    const { projectId, scheduleId } = useParams()
    const draftId = createDraftId(projectId, scheduleId)
    const { isCollapsed: codeCollapsed, onToggleCollapsed: toggleCode } = useAmendCollapse(draftId)
    const { isCollapsed: titleCollapsed, onToggleCollapsed: toggleTitle } = useAmendCollapse(draftId)
    const { isCollapsed: introContentCollapsed, onToggleCollapsed: toggleIntroContent } = useAmendCollapse(draftId)
    const { isCollapsed: introNoteCollapsed, onToggleCollapsed: toggleIntroNote } = useAmendCollapse(draftId)

    return (
        <Box>
            <ScheduleEditorFormTop.Slot />

            <Box mb={2}>
                <NewScheduleMetadataFields />
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    data-cy="schedule-code-amends-row"
                >
                    <ScheduleCodeEditableField data-cy="schedule-code-input" />
                    <AmendsBoundaryButton amendsKey={CODE_FIELD} />
                </Stack>
                <AmendsBoundaryEditor
                    amendsKey={CODE_FIELD}
                    subtitle="Change Schedule Code Reason"
                    data-cy="schedule-code-amends"
                    collapsible
                    isCollapsed={codeCollapsed}
                    onToggleCollapsed={toggleCode}
                />
                <AuthoringEditorRejectionBanner amendsKey={CODE_FIELD} />

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    data-cy="schedule-title-amends-row"
                    sx={{ mt: 2 }}
                >
                    <Typography variant="h4" component="h1">
                        <BoundEditableText field={TITLE_FIELD} preserveHtml data-cy="schedule-title-input" />
                    </Typography>
                    <AmendsBoundaryButton amendsKey={TITLE_FIELD} />
                </Stack>
                <AmendsBoundaryEditor
                    amendsKey={TITLE_FIELD}
                    subtitle="Change Schedule Title Reason"
                    data-cy="schedule-title-amends"
                    collapsible
                    isCollapsed={titleCollapsed}
                    onToggleCollapsed={toggleTitle}
                />
                <AuthoringEditorRejectionBanner amendsKey={TITLE_FIELD} />
            </Box>

            <Accordion
                title={
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        data-cy="intro-content-amends-row"
                    >
                        <strong>Introduction</strong>
                        <AmendsBoundaryButton amendsKey={INTRO_CONTENT_AMENDS_KEY} />
                    </Stack>
                }
                open={openIntro}
                onChange={setOpenIntro}
                divider
            >
                <Box mt={2} mb={3}>
                    <AuthoringBoundEditableRichText field="intro.content" />
                    <AmendsBoundaryEditor
                        amendsKey={INTRO_CONTENT_AMENDS_KEY}
                        data-cy="intro-content-amends"
                        collapsible
                        isCollapsed={introContentCollapsed}
                        onToggleCollapsed={toggleIntroContent}
                    />
                    <AuthoringEditorRejectionBanner amendsKey={INTRO_CONTENT_AMENDS_KEY} />

                    <Box sx={{ mt: 2 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            data-cy="intro-note-amends-row"
                        >
                            <Typography variant="overline">Notes</Typography>
                            <AmendsBoundaryButton amendsKey={INTRO_NOTE_AMENDS_KEY} />
                        </Stack>
                        <Box sx={{ color: isDark ? "#aaa" : "#333" }}>
                            <AuthoringBoundEditableRichText field="intro.note" />
                        </Box>
                        <AmendsBoundaryEditor
                            amendsKey={INTRO_NOTE_AMENDS_KEY}
                            subtitle="Change Note Reason"
                            data-cy="intro-note-amends"
                            collapsible
                            isCollapsed={introNoteCollapsed}
                            onToggleCollapsed={toggleIntroNote}
                        />
                        <AuthoringEditorRejectionBanner amendsKey={INTRO_NOTE_AMENDS_KEY} />
                    </Box>
                </Box>
            </Accordion>
        </Box>
    )
}
